import { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './lib';
import './Button.css';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /* hero is for the one primary action on a module home or flow footer:
     visibly larger than everything secondary around it. */
  size?: 'sm' | 'md' | 'hero';
  icon?: ReactNode;
  /* Disables the button and swaps the icon slot for a spinner. */
  loading?: boolean;
  /* Round 30 — the mute control. A disabled button that says nothing is
     the biggest defect class the product had; browsers suppress `title`
     on a disabled control, so the only explanation a person can read is
     visible text. When `disabled` and this is given, the sentence prints
     under the button (`.mrd-btn__why`), and the disabled-controls gate
     accepts it. Prefer a screen that already explains itself; use this
     where the button stands alone. */
  disabledReason?: string;
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  className,
  children,
  disabled,
  disabledReason,
  ...rest
}: ButtonProps) {
  /* An icon with no words: the one kind of button the phone's 44px width
     floor is for (utilities.css). A button with words keeps its own width
     as its floor, which an explicit min-width would take away. */
  const iconOnly = icon != null && (children === undefined || children === null || children === false || children === '');
  const button = (
    <button
      type="button"
      className={cn('mrd-btn', `mrd-btn--${variant}`, `mrd-btn--${size}`, loading && 'mrd-btn--loading', className)}
      disabled={disabled || loading}
      data-icon-only={iconOnly || undefined}
      {...rest}
    >
      {loading ? (
        <span className="mrd-btn__spinner" aria-hidden="true" />
      ) : (
        icon != null && <span className="mrd-btn__icon">{icon}</span>
      )}
      {children}
    </button>
  );
  if (!(disabled && !loading && disabledReason?.trim())) return button;
  return (
    <span className="mrd-btn-explained">
      {button}
      <span className="mrd-btn__why" role="status">
        {disabledReason}
      </span>
    </span>
  );
}
